import React, { useEffect, useRef } from "react";
import { useLang } from "../i18n";

const AZ = "/images/church_azulejo.jpg";

export default function RouteSection() {
  const { t } = useLang();
  const r = t.route;
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [r]);

  return (
    <section id="route" data-testid="route-highlights" ref={ref} className="py-28 md:py-40 bg-[#EDE3DA] relative overflow-hidden">
      <div className="watercolor-blob bg-[#8D869B]" style={{ width: 380, height: 380, top: -130, right: -110 }} />
      <div className="watercolor-blob bg-[#D8B07A]" style={{ width: 300, height: 300, bottom: -120, left: -90 }} />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        {/* Chapter header */}
        <div className="max-w-3xl reveal">
          <p className="text-xs uppercase tracking-[0.28em] text-[#5E7891] mb-6">{r.eyebrow}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.04] text-[#2D3640]">{r.title}</h2>
          <p className="mt-6 text-base md:text-lg text-[#4A5A6B] max-w-xl">{r.sub}</p>
        </div>

        {/* Opener — azulejos that remember */}
        <div className="mt-16 md:mt-24 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <figure className="md:col-span-5 reveal">
            <div className="relative aspect-[3/4] rounded-[24px] overflow-hidden border border-[#E5D9C0] shadow-[0_36px_90px_-45px_rgba(132,118,137,0.5)]">
              <img src={AZ} alt="Hand-painted azulejo tiles on a Porto facade" loading="lazy" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D3640]/20 via-transparent to-transparent" />
            </div>
          </figure>
          <div className="md:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
            <p data-testid="route-opener" className="font-serif-display italic text-3xl md:text-4xl lg:text-5xl leading-[1.18] text-[#5E7891]">
              {r.opener}
            </p>
          </div>
        </div>

        {/* Narrative chapters — a guided route, no numbers */}
        <div className="mt-20 md:mt-28 max-w-4xl relative">
          {/* vertical route line */}
          <span aria-hidden="true" className="absolute left-[8px] top-6 bottom-6 w-[2px] rounded-full bg-gradient-to-b from-[#D8B07A] via-[#D8B07A] to-[#D8B07A]/30" />
          {r.chapters.map((c, i) => (
            <div
              key={i}
              data-testid={`chapter-${i}`}
              className="reveal relative pl-10 md:pl-16 py-9 md:py-12 border-t border-[#C98C97]/25 first:border-t-0"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* circular stop marker */}
              <span aria-hidden="true" className="absolute left-0 top-[34px] md:top-[50px] w-[18px] h-[18px] rounded-full border-2 border-[#D8B07A] bg-[#EDE3DA] flex items-center justify-center">
                <span className="w-[6px] h-[6px] rounded-full bg-[#D8B07A]" />
              </span>
              <div className="grid md:grid-cols-12 gap-3 md:gap-10">
                <h3 className="md:col-span-5 font-serif-display text-3xl md:text-4xl text-[#2D3640]">{c.name}</h3>
                {c.lines.length > 0 && (
                  <div className="md:col-span-7 space-y-2">
                    {c.lines.map((ln, j) => (
                      <p key={j} className="text-lg md:text-xl text-[#4A5A6B] leading-relaxed">{ln}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Closing — what happens in between */}
        <div className="mt-20 md:mt-28 max-w-3xl reveal">
          <p data-testid="route-closing-lead" className="font-script text-3xl md:text-4xl text-[#C98C97]">{r.closingLead}</p>
          <div className="mt-6 space-y-2">
            {r.closing.map((ln, i) => (
              <p key={i} className="text-lg md:text-xl text-[#4A5A6B] leading-relaxed">{ln}</p>
            ))}
          </div>
          <p className="mt-10 font-serif-display text-2xl md:text-3xl lg:text-4xl leading-snug text-[#2D3640] max-w-2xl">{r.closingNote}</p>
        </div>
      </div>
    </section>
  );
}
